import { PolicyKey } from '../contracts/policies';
import { parseMcpGatewaySentinel, } from '../contracts/shared/file-path-labels';
/** Title-only override for `formatAlertType` — acronyms + legacy keys. */
const ALERT_TYPE_DISPLAY_NAMES = {
    untrusted_mcp_server: 'Untrusted MCP Server',
    new_mcp_server: 'New MCP Server',
    // Pre-migration legacy keys — kept so unmigrated rows display correctly.
    http_transport: 'Untrusted MCP Server',
    untrusted_source: 'Untrusted MCP Server',
    suspicious_path: 'Untrusted MCP Server',
    network_source: 'Untrusted MCP Server',
    'ai-instructions-threat': 'AI Instructions Threat',
    malicious_extension: 'Malicious Extension',
};
/**
 * Title-cases an alert type. Splits on `_` and `-` so kebab-case wire types
 * (e.g. cursor-chat `risky-code`) render correctly. `ALERT_TYPE_DISPLAY_NAMES`
 * overrides win.
 */
export function formatAlertType(type) {
    if (ALERT_TYPE_DISPLAY_NAMES[type]) {
        return ALERT_TYPE_DISPLAY_NAMES[type];
    }
    return type
        .split(/[_-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
/**
 * Shared by the canonical `toolCallScanMode` sub-policy and its two
 * pre-release aliases below, so the three cannot drift apart.
 */
const TOOL_CALL_SCAN_TYPE_DISPLAY = {
    'tool-call-scan': {
        label: 'High-Risk Tool Call Flagged',
        description: "Real-time analysis flagged a tool call the coding agent made as high-risk before or as it executed. In Enforce mode the call is blocked; in Monitor mode it's only reported. An analysis-service outage always fails open, so absence of this alert is not proof every call was scored.",
    },
};
/**
 * KIR-2381 display copy registry.
 * Keys mirror backend sub-policies and wire `type` values.
 * Source: mapping sheet (`NewTitle`, `NewWhatHappened`, `NewWhyItMatters`).
 */
export const TYPE_DISPLAY = {
    enableDependenciesScan: {
        vulnerability: {
            label: 'Vulnerable dependency in MCP server',
            description: 'An MCP server includes a package version affected by a known vulnerability. Reachability was not assessed. If the MCP server uses the affected code path, the vulnerability may be exploitable with the permissions of the user running the server.',
        },
        unknown_package: {
            label: 'Unresolved MCP server dependency',
            description: 'An MCP server references a package that does not exist on its public registry. Dependency-confusion setup. If an attacker registers the missing name first, their package gets installed on the next resolve.',
        },
        api_error: {
            label: 'MCP server dependency scan incomplete',
            description: "Dependency analysis for the MCP server failed because the upstream registry returned an error. Coverage gap, not a finding. The MCP server's dependencies won't be checked for CVEs until the scan re-runs successfully.",
        },
    },
    enableToolDescriptionsScan: {
        suspicious_description: {
            label: 'MCP server tool description matched a configured rule',
            description: "These rules are intended to find descriptions that mention sensitive access or actions. A rule match only means the text matched a configured rule. It does not confirm the tool's actual capabilities or behavior.",
        },
        permission_request: {
            label: 'MCP server tool requests elevated permissions',
            description: "Tool description explicitly asks for elevated permissions on the host. The permissions requested overshoot the tool's stated function. If the tool is ever compromised, there's more attack surface to work with than the description would suggest.",
        },
        deceptive_tool: {
            label: 'MCP server tool - name and behavior mismatch',
            description: "Tool name and described capabilities don't line up. Mismatch is associated with MCP server tool-poisoning. An agent that invokes the tool may take actions the user didn't intend.",
        },
        'ai-mcp-tool-threat': {
            label: 'Risky MCP server tool description',
            description: 'Tool metadata matched one or more risk categories: hidden instructions, name/behavior mismatch, or cross-tool coordination. When an agent invokes the tool, it may follow embedded directives or call other tools available to it.',
        },
    },
    enableHiddenCharactersScan: {
        hidden_range: {
            label: 'Invisible Unicode in MCP server config',
            description: 'An MCP server name, tool name, or description contains a run of invisible characters. Associated with prompt-injection. Can also make one MCP server name look like another in the config file.',
        },
        non_printable: {
            label: 'Non-printable character in MCP server config',
            description: 'MCP server related text contains a non-printable control character. Control characters can disrupt parsing or display in MCP server related text. Reviewers and AI systems may interpret the content differently from what is visibly displayed.',
        },
        hidden_character: {
            label: 'Invisible character in MCP server config',
            description: 'MCP server related text contains a character that does not display to the user. Used either to spoof MCP server names, or to hide directives inside content that an AI agent may interpret.',
        },
        steganography: {
            label: 'Steganographic payload in MCP server content',
            description: 'Hidden message encoded in invisible characters inside MCP server related text. Tag-character payload encoded in invisible Unicode. Can influence how an AI interprets the content while staying invisible to a reviewer.',
        },
    },
    // KIR-4215: hidden-character findings on tool-call responses forwarded from
    // a chat session (fileType `mcp-tool-response`) — content returned to the
    // agent by an MCP/built-in tool, not a file on disk. KIR-4093: the scanner
    // now emits ONLY `steganography` (a decodable tag payload) on this path. The
    // `hidden_range` (former tag-char count backstop) and `non_printable` entries
    // below are retained only to render findings persisted before KIR-4093 /
    // KIR-4094 stopped generating them — the scanner no longer emits either.
    enableToolResponseScan: {
        steganography: {
            label: 'Steganographic payload in tool output',
            description: "Hidden message encoded in invisible characters inside output a tool returned to the AI agent during a chat session. Tag-character payload encoded in invisible Unicode. Can carry directives that influence the agent's interpretation while remaining invisible to the user.",
        },
        hidden_range: {
            label: 'Invisible Unicode in tool output',
            description: "Output a tool returned to the AI agent during a chat session contains a run of invisible Unicode tag characters. Hidden text in tool output can carry prompt-injection content that influences the agent's interpretation while remaining invisible to the user.",
        },
        non_printable: {
            label: 'Non-printable character in tool output',
            description: 'Output a tool returned to the AI agent during a chat session contains non-printable control characters. Non-printable characters break parsing and display. Content the user should see ends up hidden.',
        },
    },
    enableCursorAIAnalysis: {
        'ai-detected-threat': {
            label: 'AI chat exchange flagged as risky',
            description: 'AI chat contains code, tool calls, or behavioral patterns matching known attack scenarios. Conversation patterns match scenarios where an attacker uses the AI to run code, move data, or issue destructive commands. The classifier flags the pattern match, not the intent.',
        },
    },
    maliciousPatterns: {
        'risky-code': {
            label: 'Malicious-code pattern in AI chat',
            description: 'A code block in the AI chat matches a known malicious-code pattern. If the developer accepts the suggestion, the matched code executes with their privileges. Check the matching rule for the specific behavior.',
        },
        'credential-exposure': {
            label: 'Credential in AI chat code',
            description: 'A code block in this AI chat contains a value matching a credential pattern (API key, token, or password). Hardcoded credentials can be committed to a repository or reused by anyone with access to the chat history.',
        },
        'suspicious-pattern': {
            label: 'Heuristic code-pattern match in AI chat',
            description: 'A code block in the AI chat matches a suspicious-code pattern. Pattern may flag insecure logic or backdoor behavior. Heuristic match, not a confirmed compromise - treat as a triage signal.',
        },
    },
    commandPatterns: {
        'dangerous-command': {
            label: 'Dangerous shell command in AI chat',
            description: 'A shell command in the AI chat matches a known dangerous-command pattern. If the command runs, it does what the matched pattern describes. The pattern list is curated for destructive or hostile shell behavior, not grey-area cases.',
        },
    },
    customPatterns: {
        'secret-leakage': {
            label: 'Secret detected in chat content',
            description: "The IDE-side secret scanner detected a secret in chat content before it left the developer's device. Once a secret hits chat history or vendor telemetry, anyone with access can reuse it. The IDE caught this one client-side.",
        },
    },
    mcpGatewayInjection: {
        'prompt-injection': {
            label: 'Prompt injection detected by MCP gateway',
            description: 'The MCP gateway detected prompt-injection text in MCP traffic — instructions aimed at the AI assistant rather than at the user. An upstream MCP server (or a caller behind the gateway) can steer the assistant this way without the user ever seeing the text. The gateway caught it in transit, on behalf of every client behind it.',
        },
    },
    mcpGatewayHiddenChars: {
        'hidden-characters': {
            label: 'Hidden characters detected by MCP gateway',
            description: 'The MCP gateway detected invisible characters — zero-width spaces, bidirectional overrides, or Unicode tag-block characters — in MCP traffic. They render as nothing, so instructions smuggled in them reach the AI assistant while staying invisible to the user reviewing the same text.',
        },
    },
    secretGatewayContent: {
        'secret-leakage': {
            label: 'Secret detected by MCP gateway',
            description: 'The MCP gateway detected a secret in traffic between the MCP client and an upstream MCP server. This one never touched a developer’s device — the gateway caught it in transit, on behalf of every client behind it.',
        },
    },
    secretFilePatterns: {
        'secret-leakage': {
            label: 'Secret-file path detected',
            description: 'Scanned file path matches a known secret-file pattern. Secret-file paths end up in repos and shares more often than they should. Files at these paths typically hold credentials, keys, or other authentication material - verify the contents before deciding.',
        },
    },
    enableAutoRun: {
        auto_run: {
            label: 'Auto-run enabled in AI assistant',
            description: 'Global auto-run is enabled on this AI assistant. AI-suggested commands can execute without an approval step. The user no longer has a chance to review or cancel before they run.',
        },
    },
    enableAutoFixDetection: {
        auto_fix: {
            label: 'AI assistant mode with auto-fix enabled',
            description: 'An AI assistant mode is configured to apply code fixes automatically. AI-suggested edits are applied without an explicit review step. Bad changes can reach the codebase before anyone notices.',
        },
    },
    enableAutoApplyDetection: {
        auto_apply: {
            label: 'AI assistant mode with auto-apply enabled',
            description: 'An AI assistant mode is set to auto-apply changes when no edit tool is present. Changes get written to disk without confirmation. Realistic risk: accidental edits to files the developer did not mean to change.',
        },
    },
    enableAgentAutoFix: {
        agent_auto_fix: {
            label: 'AI agent auto-fix enabled',
            description: 'Agent auto-fix is enabled on an AI assistant mode. The agent applies fixes without explicit review. For autonomous modes, this is a meaningful oversight gap.',
        },
    },
    enableGlobalAutoApprove: {
        global_auto_approve: {
            label: 'AI assistant global auto-approve enabled',
            description: 'The AI assistant is set to auto-approve every tool call globally. Every AI tool call bypasses approval - file edits, shell commands, MCP server invocations, outbound APIs. There is no per-action gate left.',
        },
    },
    enableTerminalAutoApprove: {
        terminal_auto_approve: {
            label: 'Dangerous terminal command auto-approved',
            description: 'A dangerous shell command is on the terminal auto-approve list. Commands on the list run without user approval when the AI suggests them. For things like rm, mv, or curl-piped-to-shell, that is a meaningful control to give up.',
        },
        terminal_auto_reply: {
            label: 'AI auto-replies to terminal prompts',
            description: "The AI assistant is configured to automatically answer interactive terminal prompts. Interactive 'Are you sure? [Y/n]' prompts are answered automatically. The safety check those prompts provide is removed.",
        },
    },
    enableMcpDiscovery: {
        mcp_discovery: {
            label: 'MCP server auto-discovery enabled',
            description: 'MCP server auto-discovery is enabled for this AI assistant. New MCP servers can be picked up without user action. Their tools become available to the AI on discovery.',
        },
        mcp_auto_start: {
            label: 'MCP server auto-start enabled',
            description: 'MCP server auto-start is enabled for this AI assistant. MCP servers can start on their own, with no user step. Their tools become available to the agent before anyone reviews the server itself.',
        },
    },
    enableAutoAcceptDelay: {
        auto_accept_delay: {
            label: 'AI assistant auto-accept delay shortened',
            description: "The AI assistant's auto-accept delay is shorter than the safe default. The auto-accept timer is shorter than a person can realistically catch. The cancel-before-apply window stops being a practical safeguard.",
        },
    },
    aiInstructionsAnalysis: {
        'content-size-exceeded': {
            label: 'AI-instruction file too large to scan',
            description: "The AI-instruction file exceeded the size limit. No content analysis was run. Coverage gap. The file's instructions reach the AI assistant unchecked.",
        },
        'ai-instructions-threat': {
            label: 'AI-instruction file classified as risky',
            description: "AI-instruction file (rule, skill, or command) classified as a security risk. Once loaded, the file's instructions become part of how the AI behaves. Depending on what's in it, this can cause scope drift, override safety prompts, or trigger actions the user didn't ask for.",
        },
    },
    dangerousPermissions: {
        excessive_permissions: {
            label: 'Excessive permissions in extension',
            description: 'Manifest declares permissions above the configured risk threshold. If the extension is ever compromised, the attacker inherits whatever permissions are granted. Specific capability depends on the set - clipboard, filesystem, network.',
        },
    },
    extensionsCodeScan: {
        extensions_code_scan: {
            label: 'Code-scan match in extension',
            description: "An extension code scan flagged code inside the installed extension. Extension code runs with the developer's privileges in the IDE. As a triage step, compare the flagged content against the extension's stated functionality.",
        },
    },
    extensionsPackagesOsvDetection: {
        osv_vulnerability: {
            label: 'Vulnerable dependency in extension',
            description: 'An extension includes a package version affected by a known vulnerability. Reachability was not assessed. If the extension uses the affected code path, the vulnerability may be exploitable with the permissions of the user running the extension.',
        },
    },
    extensionsMaliciousDetection: {
        unverified_publisher: {
            label: 'Extension publisher not verified',
            description: "Publisher of the installed extension did not verify against the trusted-publisher list. Without publisher verification, the displayed publisher name isn't tied to any specific identity behind the extension.",
        },
        malicious_extension: {
            label: 'Known-malicious extension installed',
            description: "The installed extension is on Kirin's known-malicious list. While installed, the extension can access the developer's source code, stored credentials, and IDE-connected services.",
        },
        suspicious_extension_name: {
            label: 'Display name matches impersonation pattern',
            description: 'Display name matches a pattern used by impersonators of trusted IDE tooling. Not proof of malicious intent on its own - but enough signal to verify the extension before trusting it.',
        },
        typosquatting: {
            label: 'Typosquatted extension installed',
            description: "Extension ID is close enough to a popular extension's ID to suggest typosquatting. Likely typosquatting. A developer aiming for the popular extension may have installed this one by mistake.",
        },
        homograph_attack: {
            label: 'Lookalike characters in extension ID',
            description: 'Extension ID contains characters visually identical to those of a known extension. Homoglyph substitution: the ID reads as the legitimate name to a human, but resolves to a different package on disk.',
        },
        mixed_script_attack: {
            label: 'Mixed-alphabet extension ID',
            description: "Extension ID mixes characters from different alphabets - pattern consistent with impersonation. Looks right to a reviewer, but at the byte level it isn't the same identifier a legitimate extension would use.",
        },
    },
    extensionsBlockedList: {
        blocked_by_policy: {
            label: 'Blocked extension installed',
            description: "The installed extension is on the organization's blocked-extensions list. Blocklist membership is an org-level decision. The criteria can be security, license, compliance, or internal preference.",
        },
    },
    mcpServersBlockedList: {
        blocked_by_policy: {
            label: 'Blocked MCP server configured',
            description: "Configured MCP server is on the organization's blocked-MCP-server list. Blocklist membership is an org-level decision. The criteria can be security, license, compliance, or internal preference.",
        },
    },
    mcpServersNewServer: {
        new_mcp_server: {
            label: 'New MCP server identified',
            description: 'An MCP server was seen in the organization for the first time. Review it in Inventory → MCP Servers and allow or block it; blocking is enforced on endpoints by a hook.',
        },
    },
    mcpServersUntrustedSource: {
        untrusted_mcp_server: {
            label: 'Untrusted MCP server source',
            description: 'MCP server source is an HTTP URL, a local file path, or an unrecognized package name. No integrity guarantee for sources without verified identity. What runs today may not be what runs tomorrow — especially for local-path or HTTP transport.',
        },
    },
    piiDetection: {
        // KIR-4211: these read as the tier's own definition, so they must match
        // `classifyPii` exactly. The previous copy asserted every item was sensitive
        // and hardcoded the count ranges, both wrong: only ONE sensitive finding is
        // required for a tier, and the boundaries are admin-configurable via the
        // `multipleThreshold` / `bulkThreshold` policy settings. Overstating the
        // trigger is itself a false-positive report generator — see the exact count
        // on the alert's "Matched PII" breakdown instead.
        'pii-detected': {
            label: 'PII Detected',
            description: 'A small number of personal-data items, at least one of them sensitive, was detected in content sent to the coding agent. Personal information can leak to third-party LLM providers or logs, outside your control.',
        },
        'multiple-pii': {
            label: 'Multiple PII Detected',
            description: 'Several personal-data items, at least one of them sensitive, were detected in a single item sent to the coding agent. Multiple personal records together raise the exposure risk if they reach a third-party model or logs. Check the matched types on the alert to see which items were sensitive.',
        },
        'bulk-pii': {
            label: 'Bulk PII Detected',
            description: 'A large volume of personal data was detected in a single item sent to the coding agent, enough to cross the bulk threshold regardless of sensitivity. Bulk exposure of personal records is high-risk and may be a reportable data leak if it reaches a third-party model or logs.',
        },
    },
    maliciousPackagesTyposquatting: {
        package_typosquatting: {
            label: 'Typosquatted Package Install',
            description: 'The coding agent tried to install a package whose name closely imitates one of the most downloaded packages in that registry, while not itself being one of them. This is the shape of a typosquatting attack: a single mistyped or model-hallucinated character installs attacker-controlled code, which then runs with the developer\u2019s privileges through the registry\u2019s install scripts.',
        },
    },
    maliciousPackagesKnownMalware: {
        malicious_package: {
            label: 'Known Malicious Package Install',
            description: 'The coding agent tried to install a package that is published in the OSV database as malware. This is not a similarity heuristic \u2014 the package has been reported and catalogued by the security community. Installing it runs the attacker\u2019s code with the developer\u2019s privileges through the registry\u2019s install scripts.',
        },
    },
    packagesBlockedList: {
        blocked_by_policy: {
            // Names the package's list, not the outcome: under Monitor the install runs.
            label: 'Block-listed Package Install',
            description: 'The coding agent tried to install a package an administrator has added to the organization\u2019s Block list. Unlike the detection policies, this is an explicit policy decision rather than a security verdict about the package itself.',
        },
    },
    skillsBlockedList: {
        blocked_by_policy: {
            // Names the administrator's decision, not the outcome: the file is
            // quarantined recoverably, not destroyed.
            label: 'Skill Marked for Delete',
            description: 'A skill an administrator marked for delete from Inventory \u2192 Skills is present on this endpoint. Unlike the detection policies, this is an explicit policy decision rather than a security verdict about the skill itself.',
        },
    },
    toolCallScanMode: TOOL_CALL_SCAN_TYPE_DISPLAY,
    // PRE-RELEASE ALIASES for rows stamped `toolCallScanDetection` (while
    // KIR-4426 modelled the scan as a standalone policy) or `enableToolCallScan`
    // (while it was a boolean toggle rather than its own Off/Monitor/Enforce
    // control) — dev/staging only (`ai_tool_call` has never been on in
    // production). Render the same copy so those rows do not fall back to the
    // unknown-sub-policy state. Delete with the backend
    // SUB_POLICY_KEYS.ToolCallScanDetectionLegacy* entries.
    toolCallScanDetection: TOOL_CALL_SCAN_TYPE_DISPLAY,
    enableToolCallScan: TOOL_CALL_SCAN_TYPE_DISPLAY,
};
/** Per-sub-policy fallback: first entry in each TYPE_DISPLAY bucket. */
export const SUB_POLICY_DISPLAY = Object.freeze(Object.fromEntries(Object.entries(TYPE_DISPLAY).map(([sp, types]) => {
    const firstKey = Object.keys(types)[0];
    if (firstKey == null) {
        throw new Error(`TYPE_DISPLAY[${sp}] has no entries`);
    }
    return [sp, types[firstKey]];
})));
/**
 * File-scope fallback for `(subPolicyKey=null, type)` findings.
 * `secret-leakage` is intentionally omitted; it falls back to legacy formatting.
 *
 * `as const satisfies` (rather than a plain annotation) so the key set is a
 * literal union: {@link NullSubPolicyMappingKey} derives from it, which is what
 * makes the mitigation table a total `Record` the compiler can check.
 *
 * @internal Exported for `scripts/generate-alert-mitigations.mjs` and the
 * drift tests only — it was module-private until the generator had to read it.
 * Not part of the supported `@kirin/api-client` surface: resolve display copy
 * through {@link formatSubPolicyDescription} and mitigations through
 * `formatSubPolicyMitigation`, both of which apply the null-bucket precedence
 * this raw table does not.
 */
export const NULL_SP_TYPE_DISPLAY = {
    hidden_range: {
        label: 'Invisible Unicode in scanned file',
        description: 'Scanned file contains a run of invisible characters. Hidden text in source or AI-instruction files can carry prompt-injection content. Also makes it easier for logic to slip past a reviewer.',
    },
    non_printable: {
        label: 'Non-printable character in scanned file',
        description: 'The file contains a non-printable control character. Non-printable characters break parsing and display. Content a reviewer should see ends up hidden.',
    },
    hidden_character: {
        label: 'Invisible character in scanned file',
        description: "The file contains a character that isn't displayed to the user. Enough to disguise a filename, URL, or identifier. The same mechanism can hide directives an AI assistant would read.",
    },
    steganography: {
        label: 'Steganographic payload in scanned file',
        description: "Hidden message encoded in invisible characters inside the scanned file. Tag-character payload encoded in invisible Unicode. Can carry directives that influence an AI's interpretation while remaining invisible during code review.",
    },
    // KIR-4213: give command-file findings the standard WHAT HAPPENED / WHY IT
    // MATTERS copy other alert types use, instead of falling back to the raw
    // classifier summary ("Classified CRITICAL (pre-scan floor HIGH). Top
    // factors:…") — internal scoring jargon, not a reviewer-facing description.
    // The specific flagged command lives in the "Command excerpt" block; the
    // plain-language risk-factor rendering is KIR-4270's Risk Assessment section.
    // `label` intentionally keeps the existing "Command File Threat" title
    // (== formatAlertType('command-file-threat')): only the description was in
    // scope for KIR-4213, not the title.
    'command-file-threat': {
        label: 'Command File Threat',
        description: 'A command file, a reusable set of instructions an AI coding agent runs on demand, was classified as a security risk. Running it makes the agent carry out the steps the file defines. Depending on what it contains, that can read secrets, reach outside the project, or run destructive shell commands the user never intended.',
    },
    // KIR-4703/4704: this is an ACTIVITY NOTIFICATION (a browser AI agent used a
    // site), not a threat detection — the copy stays event-framed and never
    // describes how the activity is attributed (that method is not surfaced to
    // customers). The site is in the Location block; Outcome distinguishes
    // Detected (observed) from Prevented (a blocked attempt).
    'browser-agent-activity': {
        label: 'AI agent site activity',
        description: 'An AI agent used this site. You are seeing this because the site is set to Monitor or Block in Agents Activity → Browser Agent Sites, so this alert is the expected notification for that choice — it is an activity record, not a threat.',
    },
};
/**
 * The lookup key for a finding, mirroring `getMappedDisplayEntry` precedence:
 * a known sub-policy keys its own bucket, anything else falls to the null
 * bucket. Keep this the single definition of that precedence — re-deriving it
 * per call site is how a lookup ends up disagreeing with the copy it renders.
 *
 * The `::` separator matches {@link TITLE_FILTER_SEPARATOR} and is spelled
 * literally here because {@link TypeDisplayMappingKey} needs it at the type
 * level; `alert-mitigations.test.ts` pins the two together.
 *
 * Not {@link encodeTitleFilterKey}, which is close but keys an unknown
 * non-null sub-policy under itself instead of falling back to the null bucket.
 */
export function resolveAlertMappingKey(subPolicyKey, type) {
    return subPolicyKey != null && isKnownSubPolicyKey(subPolicyKey)
        ? `${subPolicyKey}::${type}`
        : `::${type}`;
}
/** Backend `PolicyMeta.name` mirror (drift-guarded in `alerts.test.ts`). */
export const POLICY_DISPLAY_NAME = {
    [PolicyKey.HiddenCharacters]: 'Hidden Characters',
    [PolicyKey.McpSecurity]: 'MCP Servers Security',
    [PolicyKey.CursorDatabase]: 'Chat & Tool-Call Security',
    [PolicyKey.SecretDetection]: 'Secret Detection',
    [PolicyKey.YoloModes]: 'YOLO Modes',
    [PolicyKey.AiInstructions]: 'AI Instructions Security',
    [PolicyKey.ExtensionSecurity]: 'Extension Security Analysis',
    [PolicyKey.ExtensionsManagement]: 'Extensions',
    [PolicyKey.McpServerSources]: 'MCP Servers',
    [PolicyKey.AiInstructionCatalog]: 'Secure AI Coding Enforcement',
    [PolicyKey.AiAgentInstructions]: 'AI Agent Instructions',
    [PolicyKey.ThreatModeling]: 'Threat Modeling',
    [PolicyKey.PiiDetection]: 'PII Detection',
    [PolicyKey.MaliciousPackages]: 'Malicious Packages',
    [PolicyKey.PackagesManagement]: 'Packages',
    [PolicyKey.SkillsManagement]: 'Skills',
};
/** Backend `SUB_POLICY_TO_POLICY` mirror (drift-guarded in tests). */
export const SUB_POLICY_TO_POLICY_KEY = {
    enableDependenciesScan: PolicyKey.McpSecurity,
    enableToolDescriptionsScan: PolicyKey.McpSecurity,
    enableHiddenCharactersScan: PolicyKey.McpSecurity,
    enableToolResponseScan: PolicyKey.McpSecurity,
    mcpGatewayInjection: PolicyKey.McpSecurity,
    mcpGatewayHiddenChars: PolicyKey.McpSecurity,
    enableCursorAIAnalysis: PolicyKey.CursorDatabase,
    toolCallScanMode: PolicyKey.CursorDatabase,
    enableToolCallScan: PolicyKey.CursorDatabase,
    maliciousPatterns: PolicyKey.CursorDatabase,
    commandPatterns: PolicyKey.CursorDatabase,
    customPatterns: PolicyKey.SecretDetection,
    secretFilePatterns: PolicyKey.SecretDetection,
    secretGatewayContent: PolicyKey.SecretDetection,
    enableAutoRun: PolicyKey.YoloModes,
    enableAutoFixDetection: PolicyKey.YoloModes,
    enableAutoApplyDetection: PolicyKey.YoloModes,
    enableAgentAutoFix: PolicyKey.YoloModes,
    enableGlobalAutoApprove: PolicyKey.YoloModes,
    enableTerminalAutoApprove: PolicyKey.YoloModes,
    enableMcpDiscovery: PolicyKey.YoloModes,
    enableAutoAcceptDelay: PolicyKey.YoloModes,
    dangerousPermissions: PolicyKey.ExtensionSecurity,
    extensionsCodeScan: PolicyKey.ExtensionSecurity,
    extensionsPackagesOsvDetection: PolicyKey.ExtensionSecurity,
    extensionsMaliciousDetection: PolicyKey.ExtensionSecurity,
    extensionsBlockedList: PolicyKey.ExtensionsManagement,
    mcpServersBlockedList: PolicyKey.McpServerSources,
    mcpServersNewServer: PolicyKey.McpServerSources,
    mcpServersUntrustedSource: PolicyKey.McpServerSources,
    aiInstructionsAnalysis: PolicyKey.AiInstructions,
    piiDetection: PolicyKey.PiiDetection,
    maliciousPackagesTyposquatting: PolicyKey.MaliciousPackages,
    maliciousPackagesKnownMalware: PolicyKey.MaliciousPackages,
    packagesBlockedList: PolicyKey.PackagesManagement,
    skillsBlockedList: PolicyKey.SkillsManagement,
    toolCallScanDetection: PolicyKey.CursorDatabase,
};
/** UI sub-policy labels (mostly backend `PolicySetting.name`, drift-guarded). */
export const SUB_POLICY_DISPLAY_NAME = {
    enableDependenciesScan: 'MCP Server Dependencies',
    enableToolDescriptionsScan: 'MCP Server Tool Descriptions',
    enableHiddenCharactersScan: 'Hidden Characters',
    enableToolResponseScan: 'MCP Tool Responses',
    mcpGatewayInjection: 'MCP Gateway Prompt Injection',
    mcpGatewayHiddenChars: 'MCP Gateway Hidden Characters',
    enableCursorAIAnalysis: 'Enable Kirin AI Analysis',
    toolCallScanMode: 'Real-Time Tool-Call Analysis',
    enableToolCallScan: 'Real-Time Tool-Call Analysis',
    maliciousPatterns: 'Detected Code Rules',
    commandPatterns: 'Detected Command Rules',
    customPatterns: 'Secret Rules',
    secretFilePatterns: 'Secret File Rules',
    secretGatewayContent: 'MCP Gateway Secrets',
    enableAutoRun: 'Detect AutoRun Settings',
    enableAutoFixDetection: 'Detect AutoFix Settings',
    enableAutoApplyDetection: 'Detect Auto-Apply Settings',
    enableAgentAutoFix: 'Detect Agent AutoFix',
    enableGlobalAutoApprove: 'Detect Global Auto-Approve',
    enableTerminalAutoApprove: 'Detect Terminal Auto-Approve',
    enableMcpDiscovery: 'Detect MCP Discovery',
    enableAutoAcceptDelay: 'Detect Auto-Accept Delay',
    dangerousPermissions: 'Dangerous Permissions',
    extensionsCodeScan: 'Extensions Code Scan',
    extensionsPackagesOsvDetection: 'Dependencies Scan',
    extensionsMaliciousDetection: 'Malicious Extension',
    extensionsBlockedList: 'Blocked Extension',
    mcpServersBlockedList: 'Blocked MCP Sources',
    mcpServersNewServer: 'New MCP Servers',
    mcpServersUntrustedSource: 'Untrusted MCP Source',
    aiInstructionsAnalysis: 'AI Instructions Analysis',
    piiDetection: 'PII Detection',
    maliciousPackagesTyposquatting: 'Package Typosquatting',
    maliciousPackagesKnownMalware: 'Known Malicious Package',
    packagesBlockedList: 'Blocked Packages',
    skillsBlockedList: 'Skills Marked for Delete',
    toolCallScanDetection: 'Real-Time Tool-Call Analysis',
};
function isKnownSubPolicyKey(value) {
    return value in TYPE_DISPLAY;
}
/** Runtime lookup in `TYPE_DISPLAY[subPolicyKey][type]`. */
function getTypeDisplayEntry(subPolicyKey, type) {
    const inner = TYPE_DISPLAY[subPolicyKey];
    return inner[type];
}
function splitMappedDescription(description) {
    const separatorIndex = description.indexOf('. ');
    if (separatorIndex === -1) {
        return null;
    }
    const whatHappened = description.slice(0, separatorIndex + 1).trim();
    const whyItMatters = description.slice(separatorIndex + 2).trim();
    if (whatHappened.length === 0 && whyItMatters.length === 0) {
        return null;
    }
    return { whatHappened, whyItMatters };
}
/**
 * Marker substrings that identify a KIR-2691 *weighted* extension-security
 * body. Only `excessive_permissions` findings produced by the weighted scoring
 * engine carry a per-finding description — either the aggregate-score summary
 * (`buildAggregateFindingReason`) or a credential-targeting instant-signal
 * blurb (`CREDENTIAL_FINDING_REASON`) — that must render verbatim.
 *
 * Legacy `excessive_permissions` findings (capability-detection flag OFF, plus
 * heuristic / OSV / command-count findings that share the same type) carry a
 * terse one-liner reason and must keep their mapped explanatory copy and the
 * "what happened / why it matters" section split. Gating on the type alone
 * would wrongly strip those, so we gate on the weighted body's shape instead.
 *
 * Org-authored *custom* capability rules do NOT go through this gate: they carry
 * the distinct `custom_capability_rule` type (not `excessive_permissions`) and
 * have no `TYPE_DISPLAY` entry, so their authored reason already renders verbatim
 * via the unmapped fallback — no marker sniffing needed (KIR-2691 §7).
 *
 * Kept in sync with the backend extension-security scanner.
 */
export const WEIGHTED_EXTENSION_BODY_MARKERS = [
    'Aggregate score ',
    'Flagged even for trusted publishers.',
];
export function isDynamicExtensionBody(type, description) {
    return (type === 'excessive_permissions' &&
        WEIGHTED_EXTENSION_BODY_MARKERS.some(marker => description.includes(marker)));
}
function getMappedDisplayEntry(subPolicyKey, type) {
    if (subPolicyKey != null && isKnownSubPolicyKey(subPolicyKey)) {
        return getTypeDisplayEntry(subPolicyKey, type);
    }
    // Widening cast for the same reason `getTypeDisplayEntry` applies one to
    // `TYPE_DISPLAY`: `as const satisfies` makes the keys a literal union, which
    // a `string` variable cannot index under strict.
    return NULL_SP_TYPE_DISPLAY[type];
}
/** Label lookup precedence: exact mapped pair -> legacy fallback. */
export function formatSubPolicyLabel(subPolicyKey, fallbackType) {
    const mappedEntry = getMappedDisplayEntry(subPolicyKey, fallbackType);
    if (mappedEntry != null) {
        return mappedEntry.label;
    }
    return formatAlertType(fallbackType);
}
/**
 * Hidden-characters finding types whose per-finding description is a
 * scanner-computed character identity (e.g. "Left-to-Right Mark (U+200E)",
 * "Directional marks (U+200E)", "Non-printable character (0x01)"). The static
 * mapped copy must not swallow it (KIR-4094) — it rides along as
 * {@link AlertDescriptionSections.detected}.
 */
const HIDDEN_CHARACTER_DETAIL_TYPES = new Set([
    'hidden_range',
    'non_printable',
    'hidden_character',
    'steganography',
]);
/**
 * Whether an alert comes from the hidden-characters scanner, and so carries a
 * `matchContext` holding the surrounding-text excerpt (KIR-4229) rather than
 * the secret-leakage snippet that shares the field. Callers rendering
 * `matchContext` must branch on this: the secret one is flag-gated and belongs
 * to a different panel.
 *
 * The single definition of that type set for both sides. The backend gates its
 * ingest PII screen and its DTO exposure on this same predicate — two copies
 * drifting apart would expose an excerpt the screen never saw.
 */
export function isHiddenCharacterAlertType(type) {
    return HIDDEN_CHARACTER_DETAIL_TYPES.has(type);
}
/**
 * Markers the excerpt builder substitutes for invisible runs — `[U+200E]`, or
 * `[787 hidden characters: U+E0001…U+E007F]` (`787+` when the scan window cut
 * the run short). Produced by `apps/backend/src/policies/hidden/snippet.ts`,
 * whose tests assert its output matches this pattern, so the two cannot drift.
 */
const HIDDEN_CHARACTER_MARKER = /\[U\+[0-9A-F]{4,6}\]|\[\d+\+? hidden characters: [^\]]*\]/g;
/**
 * Split a KIR-4229 excerpt into alternating text and marker segments so a
 * renderer can highlight the flagged region. One non-hidden segment when the
 * excerpt has no marker (a legacy value).
 */
export function splitHiddenCharacterExcerpt(excerpt) {
    const segments = [];
    let cursor = 0;
    // Fresh lastIndex per call — the regex is module-level and /g is stateful.
    const pattern = new RegExp(HIDDEN_CHARACTER_MARKER.source, 'g');
    for (let match = pattern.exec(excerpt); match != null; match = pattern.exec(excerpt)) {
        if (match.index > cursor) {
            segments.push({
                text: excerpt.slice(cursor, match.index),
                hidden: false,
            });
        }
        segments.push({ text: match[0], hidden: true });
        cursor = match.index + match[0].length;
    }
    if (cursor < excerpt.length) {
        segments.push({ text: excerpt.slice(cursor), hidden: false });
    }
    return segments;
}
/**
 * The hidden-characters scanner appends the decoded steganography payload to
 * the issue location as `\nMessage: "<decoded>"` (see
 * hiddenCharactersPolicyScanner). Greedy match so decoded quotes survive.
 */
const STEGO_LOCATION_MESSAGE = /\nMessage: "([\s\S]*)"$/;
/** Backend stem when Kirin AI returns no summary — nothing a triager can act on. */
const AI_ANALYSIS_FALLBACK_SUMMARY = 'AI detected potential security concerns';
/**
 * Returns sectioned mapped content for alert descriptions.
 * Null means "treat as unmapped" and render raw fallback description.
 */
export function formatSubPolicySections(subPolicyKey, type, description, location) {
    // Weighted extension-security bodies render their per-finding description
    // verbatim (no "what happened / why it matters" split from static copy).
    if (description != null && isDynamicExtensionBody(type, description)) {
        return null;
    }
    const mappedEntry = getMappedDisplayEntry(subPolicyKey, type);
    if (mappedEntry == null) {
        return null;
    }
    const sections = splitMappedDescription(mappedEntry.description);
    if (sections != null && HIDDEN_CHARACTER_DETAIL_TYPES.has(type)) {
        // For steganography the finding description is static copy; the actual
        // identity is the decoded payload carried on the location (PR #2315 P3).
        const detected = type === 'steganography'
            ? location?.match(STEGO_LOCATION_MESSAGE)?.[1]?.trim()
            : description?.trim();
        if (detected != null && detected.length > 0) {
            return { ...sections, detected };
        }
    }
    if (sections != null && type === 'ai-instructions-threat') {
        // KIR-4214: the classifier's summary is the only per-finding signal this
        // alert carries — the static copy must not swallow it.
        const detected = description?.trim();
        if (detected != null &&
            detected.length > 0 &&
            !detected.startsWith(AI_ANALYSIS_FALLBACK_SUMMARY)) {
            return { ...sections, detected };
        }
    }
    if (sections != null && type === 'tool-call-scan') {
        // The finding description names the kirin-ai threat category. Static copy
        // must not swallow it — same carve-out as AI-instructions (KIR-4214).
        const detected = description?.trim();
        if (detected != null && detected.length > 0) {
            return { ...sections, detected };
        }
    }
    if (sections != null && type === 'suspicious_description') {
        // KIR-4175: the finding description is the matched rule's reason
        // ("May handle sensitive credentials") — the one per-finding signal that
        // says WHICH rule fired. Surface it as the Detected section so the operator
        // sees it on the card without expanding source-details or needing the
        // alert_source_details flag (the static KIR-4176 body would otherwise
        // swallow it). Same carve-out as hidden-characters (KIR-4094) and
        // AI-instructions (KIR-4214).
        const detected = description?.trim();
        if (detected != null && detected.length > 0) {
            return { ...sections, detected };
        }
    }
    return sections;
}
/**
 * Surface-specific copy for MCP gateway alerts.
 * FilePath sentinel is `mcp:<surface>:<tool>` (KIR-3287); surface drives the
 * copy. Keyed by {@link McpGatewaySurface} so this table can't silently drift
 * from the File-column label table (MCP_SURFACE_LABELS in file-path-labels.ts)
 * on which surfaces are known.
 */
const MCP_SURFACE_SECTIONS = {
    'tool-args': {
        whatHappened: 'The MCP gateway detected a secret in a tool argument before it reached the upstream server.',
        whyItMatters: 'The secret was about to be sent to an MCP server. If undetected, the upstream server — and anyone with access to its logs — would have received it.',
    },
    'tool-result': {
        whatHappened: 'The MCP gateway detected a secret in a tool result returned by the upstream server.',
        whyItMatters: 'An MCP server returned a secret in its response. The MCP client and the AI assistant would have read it — and may quote or act on it.',
    },
    'prompt-args': {
        whatHappened: 'The MCP gateway detected a secret in a prompt-fetch request before it reached the upstream server.',
        whyItMatters: 'A secret was embedded in a prompt-fetch argument heading to the server. The upstream server would have received it in the request.',
    },
    'prompt-result': {
        whatHappened: 'The MCP gateway detected a secret in a rendered prompt returned by the upstream server.',
        whyItMatters: 'A secret is embedded in content the AI assistant is about to read. Prompt-injection surface — the upstream server controls what the AI sees.',
    },
    'tool-description': {
        whatHappened: 'The MCP gateway detected an indirect prompt-injection payload embedded in a tool’s description (its metadata), before the tool was trusted by the AI agent.',
        whyItMatters: 'A poisoned tool looks legitimate to anyone checking only its name or schema — the malicious instructions hide in the description the agent reads to decide how to use it. Flagging it at the metadata layer stops the attack before any call is ever made.',
    },
};
const MCP_SURFACE_FALLBACK = {
    whatHappened: 'The MCP gateway detected a secret as it crossed the gateway.',
    whyItMatters: 'The secret was in transit between the MCP client and an upstream MCP server.',
};
/**
 * The alert type each surface's copy above describes. The gateway routes several
 * finding kinds through one sentinel, so the pairing is what makes the copy
 * safe: the four in-band surfaces speak about a leaked secret, `tool-description`
 * about an injected instruction in tool metadata (KIR-3617). A surface paired
 * with any other type gets no copy from this table.
 */
const MCP_SURFACE_ALERT_TYPE = {
    'tool-args': 'secret-leakage',
    'tool-result': 'secret-leakage',
    'prompt-args': 'secret-leakage',
    'prompt-result': 'secret-leakage',
    'tool-description': 'suspicious_description',
};
/**
 * Returns surface-specific sections for an MCP gateway alert whose type matches
 * what that surface's copy describes (`MCP_SURFACE_ALERT_TYPE`), or null. The
 * gateway emits prompt-injection / hidden-characters findings through the same
 * `mcp:<surface>:<tool>` sentinel as secrets, and each entry's copy names one
 * kind — a mismatched pair must fall back to its own policy sections instead of
 * borrowing this copy.
 *
 * Also returns null when `filePath` is not an `mcp:<surface>:<tool>` sentinel
 * (KIR-3287) at all, so an ordinary local-file secret is never described as a
 * gateway detection. That guard is intrinsic on purpose: the caller must not
 * have to know the sentinel format. Note `null` (not a gateway alert) differs
 * from a parsed-but-surfaceless sentinel (legacy `mcp:<tool>`), which still
 * gets the generic gateway copy — secret-only, since that copy names a secret.
 */
export function formatMcpGatewaySections(filePath, type) {
    const parsed = parseMcpGatewaySentinel(filePath);
    if (parsed === null) {
        return null;
    }
    if (parsed.surface === '') {
        return type === 'secret-leakage' ? MCP_SURFACE_FALLBACK : null;
    }
    return type === MCP_SURFACE_ALERT_TYPE[parsed.surface]
        ? MCP_SURFACE_SECTIONS[parsed.surface]
        : null;
}
/** Mapped pair -> mapped description; unmapped pair -> raw fallback description. */
export function formatSubPolicyDescription(subPolicyKey, type, fallbackDescription) {
    // Weighted extension-security bodies (e.g. excessive_permissions) carry a
    // per-finding description generated at scan time; never override it with
    // static copy. Legacy/heuristic findings of the same type fall through to
    // the mapped description below.
    if (isDynamicExtensionBody(type, fallbackDescription)) {
        return fallbackDescription;
    }
    const mappedEntry = getMappedDisplayEntry(subPolicyKey, type);
    if (mappedEntry != null) {
        return mappedEntry.description;
    }
    return fallbackDescription;
}
/**
 * Single-owner type -> sub-policy mapping for filter labels.
 * Multi-owner / dual-scope types are intentionally excluded and fall back.
 */
export const TYPE_TO_SUB_POLICY_KEY = {
    vulnerability: 'enableDependenciesScan',
    unknown_package: 'enableDependenciesScan',
    api_error: 'enableDependenciesScan',
    suspicious_description: 'enableToolDescriptionsScan',
    permission_request: 'enableToolDescriptionsScan',
    deceptive_tool: 'enableToolDescriptionsScan',
    'ai-mcp-tool-threat': 'enableToolDescriptionsScan',
    // Gateway-exclusive types (the ContextForge plugin's own issue types), so
    // single-owner despite kirin having its own hidden-character scanners under
    // snake_case types.
    'prompt-injection': 'mcpGatewayInjection',
    'hidden-characters': 'mcpGatewayHiddenChars',
    'ai-detected-threat': 'enableCursorAIAnalysis',
    'risky-code': 'maliciousPatterns',
    'credential-exposure': 'maliciousPatterns',
    'suspicious-pattern': 'maliciousPatterns',
    'dangerous-command': 'commandPatterns',
    auto_run: 'enableAutoRun',
    auto_fix: 'enableAutoFixDetection',
    auto_apply: 'enableAutoApplyDetection',
    agent_auto_fix: 'enableAgentAutoFix',
    global_auto_approve: 'enableGlobalAutoApprove',
    terminal_auto_approve: 'enableTerminalAutoApprove',
    terminal_auto_reply: 'enableTerminalAutoApprove',
    mcp_discovery: 'enableMcpDiscovery',
    mcp_auto_start: 'enableMcpDiscovery',
    auto_accept_delay: 'enableAutoAcceptDelay',
    'content-size-exceeded': 'aiInstructionsAnalysis',
    'ai-instructions-threat': 'aiInstructionsAnalysis',
    excessive_permissions: 'dangerousPermissions',
    unverified_publisher: 'extensionsMaliciousDetection',
    malicious_extension: 'extensionsMaliciousDetection',
    suspicious_extension_name: 'extensionsMaliciousDetection',
    typosquatting: 'extensionsMaliciousDetection',
    homograph_attack: 'extensionsMaliciousDetection',
    mixed_script_attack: 'extensionsMaliciousDetection',
    osv_vulnerability: 'extensionsPackagesOsvDetection',
    extensions_code_scan: 'extensionsCodeScan',
    untrusted_mcp_server: 'mcpServersUntrustedSource',
    new_mcp_server: 'mcpServersNewServer',
    'pii-detected': 'piiDetection',
    'multiple-pii': 'piiDetection',
    'bulk-pii': 'piiDetection',
    package_typosquatting: 'maliciousPackagesTyposquatting',
    malicious_package: 'maliciousPackagesKnownMalware',
    'tool-call-scan': 'toolCallScanMode',
};
/**
 * Filter label formatter:
 * - revamp off: legacy `formatAlertType`
 * - revamp on: mapped label for single-owner types, legacy fallback otherwise
 */
export function formatTypeFilterLabel(type, revampEnabled) {
    if (!revampEnabled) {
        return formatAlertType(type);
    }
    const subPolicyKey = TYPE_TO_SUB_POLICY_KEY[type];
    if (subPolicyKey == null) {
        return formatAlertType(type);
    }
    return formatSubPolicyLabel(subPolicyKey, type);
}
/**
 * KIR-3769: separator between the sub-policy key and the wire `type` in a
 * composite Alerts "Title" filter key. Chosen because it cannot occur inside a
 * subPolicyKey (camelCase identifiers) or a `type` slug (kebab/snake case), so
 * `decodeTitleFilterKey` can split unambiguously on its first occurrence.
 */
export const TITLE_FILTER_SEPARATOR = '::';
/**
 * Encodes a (subPolicyKey, type) pair into the composite Title filter key sent
 * to / returned from the backend. A null/empty subPolicyKey yields `::<type>`,
 * matching the backend's `coalesce(sub_policy_key, '')` expression.
 */
export function encodeTitleFilterKey(subPolicyKey, type) {
    return `${subPolicyKey ?? ''}${TITLE_FILTER_SEPARATOR}${type}`;
}
/**
 * Decodes a composite Title filter key (`<subPolicyKey>::<type>`) back into its
 * parts. An empty sub-policy prefix decodes to `subPolicyKey: null`. A key with
 * no separator is treated as a bare `type` (defensive — should not happen).
 */
export function decodeTitleFilterKey(composite) {
    const separatorIndex = composite.indexOf(TITLE_FILTER_SEPARATOR);
    if (separatorIndex === -1) {
        return { subPolicyKey: null, type: composite };
    }
    const rawSubPolicyKey = composite.slice(0, separatorIndex);
    const type = composite.slice(separatorIndex + TITLE_FILTER_SEPARATOR.length);
    return {
        subPolicyKey: rawSubPolicyKey.length > 0 ? rawSubPolicyKey : null,
        type,
    };
}
/**
 * Title-filter label formatter: decodes a composite `<subPolicyKey>::<type>`
 * option value and renders it through the SAME `formatSubPolicyLabel` used by
 * the Title table cell, so an option's label is identical to the cell it
 * filters. Slugs with no `TYPE_DISPLAY` entry fall back to `formatAlertType`
 * (handled inside `formatSubPolicyLabel`), keeping them visible + searchable.
 */
export function formatTitleFilterLabel(composite) {
    const { subPolicyKey, type } = decodeTitleFilterKey(composite);
    return formatSubPolicyLabel(subPolicyKey, type);
}
/**
 * Renders a dashboard Top Alerts row's composite `<subPolicyKey>::<type>` key
 * as the label the user sees, matching whichever Alerts-page column the row
 * deep-links to: the composite Title cell with `alerts_revamp` on, the plain
 * Type cell with it off.
 *
 * Every surface that names a Top Alerts row must go through this — the widget
 * cell and the Users drill-down heading. They drifted once (the heading always
 * used the mapped title, so a row reading "Steganography" opened a modal titled
 * "Steganographic payload in scanned file"), which is the whole reason this is
 * one function instead of a repeated ternary.
 *
 * @param alertName - Composite title key from the top-alerts endpoint.
 * @param revampEnabled - `alerts_revamp` state; picks the Title or Type label.
 * @returns Human-readable label for the row.
 */
export function formatTopAlertLabel(alertName, revampEnabled) {
    return revampEnabled
        ? formatTitleFilterLabel(alertName)
        : formatAlertType(decodeTitleFilterKey(alertName).type);
}
/**
 * Parses a route splat param for alerts/.../occurrences routes. Returns the
 * alertKey when the path is "alertKey/occurrences" (after normalizing),
 * else undefined. Trims empty segments so trailing/duplicate slashes work.
 * React Router already decodes params['*'], so no decodeURIComponent here.
 */
export function parseOccurrencesAlertKey(splat) {
    if (!splat)
        return undefined;
    const parts = splat
        .trim()
        .split('/')
        .filter(segment => segment.length > 0);
    return parts.length === 2 && parts[1] === 'occurrences'
        ? parts[0]
        : undefined;
}
/**
 * KIR-2788: parses a route splat for the unified notification landing route
 * `/{org}/alerts/{token}`. Every notification — single, composite, or secret —
 * deep-links to exactly one opaque token (a 64-hex finding_key or alert-set id);
 * the backend resolves either. Returns the token when the path is exactly one
 * segment; else undefined (the empty splat is the list, `{token}/occurrences` is
 * the occurrences modal — see {@link parseOccurrencesAlertKey}). React Router
 * already decodes params['*'], so no decodeURIComponent here.
 */
export function parseAlertToken(splat) {
    if (!splat)
        return undefined;
    const parts = splat
        .trim()
        .split('/')
        .filter(segment => segment.length > 0);
    return parts.length === 1 ? parts[0] : undefined;
}
//# sourceMappingURL=alerts.js.map